import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Search, Loader2, Building2, IdCard, Hash, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const ICON = { cliente: Building2, nif: IdCard, serie: Hash, numero: Hash, transportista: Building2 };

/**
 * Buscador rico con autocompletado — sugerencias de clientes, NIF y series.
 * onSelect(suggestion): callback con {kind, label, apply_filters}
 * value/onChange manejan el texto libre para búsqueda normal.
 */
export default function SmartSearch({ value, onChange, onSelect, onSubmit, getSuggestions, placeholder = "Nº, cliente, NIF, serie…", testid = "smart-search", id }) {
  const [sugs, setSugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const timerRef = useRef();
  const boxRef = useRef();

  useEffect(() => {
    if (!value || value.length < 1) { setSugs([]); setOpen(false); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        let list = [];
        if (getSuggestions) {
          list = (await Promise.resolve(getSuggestions(value))) || [];
        } else {
          const { data } = await api.get("/api/v1/albaranes/suggest", { params: { q: value } });
          list = data.suggestions || [];
        }
        setSugs(list);
        setOpen(list.length > 0);
        setHighlight(-1);
      } catch (_e) { setSugs([]); setOpen(false); }
      finally { setLoading(false); }
    }, getSuggestions ? 80 : 220);
    return () => clearTimeout(timerRef.current);
  }, [value, getSuggestions]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (s) => {
    setOpen(false);
    onSelect?.(s);
  };

  const handleKey = (e) => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      if (open && sugs.length > 0 && highlight >= 0) pick(sugs[highlight]);
      else { setOpen(false); onSubmit?.(value); }
      return;
    }
    if (!open || sugs.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % sugs.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + sugs.length) % sugs.length); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.75} />
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => sugs.length > 0 && setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className="pl-8 pr-8"
          data-testid={testid}
        />
        {loading ? (
          <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
        ) : value ? (
          <button type="button" aria-label="Limpiar" onClick={() => { onChange(""); setSugs([]); setOpen(false); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-slate-100 flex items-center justify-center" data-testid={`${testid}-clear`}>
            <X className="h-3.5 w-3.5 text-slate-500" />
          </button>
        ) : null}
      </div>

      {open && sugs.length > 0 ? (
        <div className="absolute z-30 mt-1 w-full max-h-80 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-list`}>
          {sugs.map((s, i) => {
            const Icon = ICON[s.kind] || Building2;
            return (
              <button
                type="button"
                key={`${s.kind}-${s.label}-${i}`}
                onClick={() => pick(s)}
                onMouseEnter={() => setHighlight(i)}
                data-testid={`${testid}-item-${i}`}
                className={`w-full text-left px-3 py-2 flex items-start gap-3 border-b border-slate-100 last:border-b-0 ${i === highlight ? "bg-blue-50" : "hover:bg-slate-50"}`}
              >
                <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${
                  s.kind === "cliente" ? "bg-blue-50 text-blue-700 border border-blue-100"
                  : s.kind === "nif" ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                  : "bg-amber-50 text-amber-700 border border-amber-100"
                }`}>
                  <Icon className="h-3.5 w-3.5"/>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{s.label}</div>
                  <div className="text-[11px] text-slate-500 truncate font-mono">{s.sub}</div>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400 pt-1">{s.kind}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
